import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  CircularProgress,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { useGhgOrganization } from 'src/common/hooks';
import QuestionnaireTitle from 'src/views/components/Questionnaire/Title';
import {
  PageContainer,
  SectionGroupContainer,
} from 'src/views/Pages/Questionnaire/index.styles';
import { StyledPanel } from 'src/components/StyledPanel';
import {
  GhgMediaImportHistoryItem,
  GhgMediaImportSummary,
  useDeleteGhgMediaImportRun,
  useGhgMediaImport,
  useGhgMediaImportHistory,
} from '../hooks';

const CURRENT_YEAR = new Date().getFullYear();

const formatHistoryDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

export default function GhgMediaImportPage() {
  const { t } = useTranslation();
  const organization = useGhgOrganization();
  const mediaImport = useGhgMediaImport();
  const mediaImportHistory = useGhgMediaImportHistory(organization.id);
  const deleteMediaImportRun = useDeleteGhgMediaImportRun();

  const [year, setYear] = React.useState(String(CURRENT_YEAR));
  const [includeAuditWorkbook, setIncludeAuditWorkbook] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [summary, setSummary] = React.useState<GhgMediaImportSummary | null>(
    null
  );
  const [runPendingDelete, setRunPendingDelete] =
    React.useState<GhgMediaImportHistoryItem | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedFile) {
      toast.error(t('ghg:mediaImport.validation.fileRequired') as string);
      return;
    }

    const parsedYear = parseInt(year, 10);
    if (!Number.isInteger(parsedYear) || year.length !== 4) {
      toast.error(t('ghg:mediaImport.validation.yearRequired') as string);
      return;
    }

    const result = await mediaImport.mutateAsync({
      organizationId: organization.id,
      year: parsedYear,
      includeAuditWorkbook,
      file: selectedFile,
    });

    if (result.type === 'summary') {
      setSummary(result.summary);
      toast.success(t('ghg:mediaImport.toasts.success') as string);
      return;
    }

    setSummary(null);
    toast.success(t('ghg:mediaImport.toasts.auditDownloaded') as string);
  };

  const onDeleteImportRun = async () => {
    if (!runPendingDelete) return;

    await deleteMediaImportRun.mutateAsync({
      organizationId: organization.id,
      historyId: runPendingDelete.id,
    });
    setRunPendingDelete(null);
    toast.success(t('ghg:mediaImport.toasts.historyDeleted') as string);
  };

  return (
    <PageContainer>
      <Dialog open={mediaImport.isLoading} maxWidth="xs" fullWidth>
        <DialogContent>
          <Stack
            spacing={2}
            alignItems="center"
            textAlign="center"
            sx={{ py: 2 }}
          >
            <CircularProgress />
            <Typography variant="h6">
              {t('ghg:mediaImport.loadingModal.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('ghg:mediaImport.loadingModal.description')}
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={runPendingDelete !== null}
        onClose={() => setRunPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('ghg:mediaImport.history.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('ghg:mediaImport.history.deleteDescription', {
              runTag: runPendingDelete?.run_tag || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunPendingDelete(null)}>
            {t('ghg:mediaImport.history.cancel')}
          </Button>
          <Button
            color="error"
            onClick={onDeleteImportRun}
            disabled={deleteMediaImportRun.isLoading}
          >
            {t('ghg:mediaImport.history.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <SectionGroupContainer>
        <QuestionnaireTitle text={t('ghg:mediaImport.title')} />
        <StyledPanel>
          <Stack
            component="form"
            spacing={3}
            onSubmit={onSubmit}
            sx={{ maxWidth: 720 }}
          >
            <Typography variant="body1">
              {t('ghg:mediaImport.description')}
            </Typography>

            <TextField
              label={t('ghg:mediaImport.fields.year')}
              type="number"
              value={year}
              onChange={(event) => setYear(event.target.value)}
              inputProps={{ min: 1900, max: 3000 }}
              required
            />

            <Stack spacing={1}>
              <Button variant="outlined" component="label">
                {selectedFile
                  ? t('ghg:mediaImport.fields.changeFile')
                  : t('ghg:mediaImport.fields.file')}
                <input
                  hidden
                  type="file"
                  accept=".xlsx"
                  onChange={(event) =>
                    setSelectedFile(event.target.files?.[0] || null)
                  }
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedFile?.name ||
                  t('ghg:mediaImport.fields.noFileSelected')}
              </Typography>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAuditWorkbook}
                  onChange={(event) =>
                    setIncludeAuditWorkbook(event.target.checked)
                  }
                />
              }
              label={t('ghg:mediaImport.fields.includeAuditWorkbook')}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={mediaImport.isLoading}
            >
              {mediaImport.isLoading
                ? t('ghg:mediaImport.actions.processing')
                : t('ghg:mediaImport.actions.submit')}
            </Button>

            {summary && (
              <Alert severity="success">
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    {t('ghg:mediaImport.summary.title')}
                  </Typography>
                  <Typography variant="body2">
                    {t('ghg:mediaImport.summary.runTag', {
                      runTag: summary.run_tag,
                    })}
                  </Typography>
                  <Typography variant="body2">
                    {t('ghg:mediaImport.summary.counts', {
                      prepared: summary.prepared_fragments,
                      aggregated: summary.aggregated_groups,
                      imported: summary.imported_groups,
                      omitted: summary.omitted_groups,
                    })}
                  </Typography>
                </Stack>
              </Alert>
            )}
          </Stack>
        </StyledPanel>

        <StyledPanel>
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('ghg:mediaImport.history.title')}
            </Typography>

            {mediaImportHistory.data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('ghg:mediaImport.history.empty')}
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {t('ghg:mediaImport.history.columns.date')}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {t('ghg:mediaImport.history.columns.file')}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {t('ghg:mediaImport.history.columns.year')}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {t('ghg:mediaImport.history.columns.imported')}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {t('ghg:mediaImport.history.columns.omitted')}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', width: 1 }}
                      >
                        {t('ghg:mediaImport.history.columns.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mediaImportHistory.data.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatHistoryDate(item.created_at)}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {item.source_filename}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {item.target_year}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {item.imported_groups}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {item.omitted_groups}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            color="error"
                            size="small"
                            onClick={() => setRunPendingDelete(item)}
                          >
                            {t('ghg:mediaImport.history.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </StyledPanel>
      </SectionGroupContainer>
    </PageContainer>
  );
}
